import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import ImageUpload from '../../components/admin/ImageUpload';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL, getUploadUrl } from '../../config/api';
import './css/AdminPages.css';

const AdminHeroImages = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoFilename, setLogoFilename] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image: '',
    display_order: 0,
    is_active: true,
    title_ar: '',
    title_en: '',
    link_url: ''
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();

  useEffect(() => {
    fetchHeroImages();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      const res = await axios.get(`${API_URL}/api/settings/logo`);
      setLogoFilename(res.data?.logo || '');
    } catch (error) {
      console.error('Error fetching logo:', error);
    } finally {
      setLogoLoading(false);
    }
  };

  const fetchHeroImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/hero-images/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHeroImages(response.data);
    } catch (error) {
      console.error('Error fetching hero images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLogo = async () => {
    if (!logoFilename) return;
    try {
      setLogoSaving(true);
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/api/settings/logo`,
        { logo: logoFilename },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(t('admin.logoUpdated') || 'Logo updated successfully');
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error updating logo');
    } finally {
      setLogoSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editingImage) {
        await axios.put(
          `${API_URL}/api/hero-images/${editingImage.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/hero-images`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      fetchHeroImages();
      handleCloseModal();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error saving hero image');
    }
  };

  const handleEdit = (heroImage) => {
    setEditingImage(heroImage);
    setFormData({
      image: heroImage.image || '',
      display_order: heroImage.display_order || 0,
      is_active: heroImage.is_active !== undefined ? heroImage.is_active : true,
      title_ar: heroImage.title_ar || '',
      title_en: heroImage.title_en || '',
      link_url: heroImage.link_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteConfirm') || 'Are you sure you want to delete this image?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/hero-images/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchHeroImages();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error deleting hero image');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingImage(null);
    setFormData({
      image: '',
      display_order: 0,
      is_active: true,
      title_ar: '',
      title_en: '',
      link_url: ''
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>{t('admin.loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="admin-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <ArrowLeftIcon className="icon" />
          {t('admin.back') || 'Back'}
        </button>
        <h1>{t('admin.heroImages') || 'Hero Images'}</h1>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={() => setShowModal(true)} className="add-btn">
            + {t('admin.add') || 'Add'} {t('admin.heroImage') || 'Hero Image'}
          </button>
        </div>
      </div>

      {/* Logo management */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3>{t('admin.logo') || 'Website Logo'}</h3>
        </div>
        <div className="card-body" style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 8px', color: '#666' }}>
              {t('admin.logoHint') || 'Upload a logo. Recommended: transparent PNG/WebP.'}
            </p>
            <ImageUpload
              onUploadComplete={(filename) => setLogoFilename(filename)}
              currentImage={logoFilename ? getUploadUrl(logoFilename) : null}
            />
            {logoFilename && (
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                {t('admin.currentLogo') || 'Current logo'}: {logoFilename}
              </p>
            )}
            <button
              className="save-btn"
              style={{ marginTop: '10px' }}
              onClick={handleSaveLogo}
              disabled={logoSaving || !logoFilename}
            >
              {logoSaving ? (t('admin.saving') || 'Saving...') : (t('admin.saveLogo') || 'Save Logo')}
            </button>
          </div>
          <div style={{ minWidth: '160px' }}>
            <p style={{ margin: '0 0 8px', color: '#666' }}>{t('admin.preview') || 'Preview'}</p>
            <div style={{ border: '1px solid #eee', borderRadius: '8px', padding: '12px', background: '#fafafa' }}>
              {logoLoading ? (
                <p style={{ color: '#888' }}>{t('admin.loading') || 'Loading...'}</p>
              ) : logoFilename ? (
                <img
                  src={getUploadUrl(logoFilename)}
                  alt="Logo preview"
                  style={{ maxWidth: '140px', maxHeight: '120px', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                />
              ) : (
                <p style={{ color: '#888' }}>{t('admin.noLogo') || 'No logo set'}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.id') || 'ID'}</th>
              <th>{t('admin.image') || 'Image'}</th>
              <th>{t('admin.titleAr') || 'Title (AR)'}</th>
              <th>{t('admin.titleEn') || 'Title (EN)'}</th>
              <th>{t('admin.link') || 'Link URL'}</th>
              <th>{t('admin.order') || 'Order'}</th>
              <th>{t('admin.active') || 'Active'}</th>
              <th>{t('admin.actions') || 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {heroImages.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                  {t('admin.noHeroImages') || 'No hero images found. Add your first image!'}
                </td>
              </tr>
            ) : (
              heroImages.map((img) => (
                <tr key={img.id}>
                  <td>{img.id}</td>
                  <td>
                    {img.image && (
                      <img 
                        src={getUploadUrl(img.image)} 
                        alt={img.title_ar || img.title_en || 'Hero Image'} 
                        className="table-image"
                        style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'cover' }}
                      />
                    )}
                  </td>
                  <td>{img.title_ar || '-'}</td>
                  <td>{img.title_en || '-'}</td>
                  <td>
                    {img.link_url ? (
                      <a href={img.link_url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                        {img.link_url.length > 30 ? img.link_url.substring(0, 30) + '...' : img.link_url}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{img.display_order}</td>
                  <td>{img.is_active ? (t('admin.yes') || 'Yes') : (t('admin.no') || 'No')}</td>
                  <td>
                    <button onClick={() => handleEdit(img)} className="edit-btn">
                      {t('admin.edit') || 'Edit'}
                    </button>
                    <button onClick={() => handleDelete(img.id)} className="delete-btn">
                      {t('admin.delete') || 'Delete'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingImage ? (t('admin.editHeroImage') || 'Edit Hero Image') : (t('admin.addHeroImage') || 'Add Hero Image')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('admin.image') || 'Image'} *</label>
                <ImageUpload
                  onUploadComplete={(filename) => setFormData({ ...formData, image: filename })}
                  currentImage={formData.image ? getUploadUrl(formData.image) : null}
                />
                {formData.image && (
                  <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    Current: {formData.image}
                  </p>
                )}
              </div>
              <div className="form-group">
                <label>{t('admin.titleAr') || 'Title (Arabic)'}</label>
                <input
                  type="text"
                  value={formData.title_ar}
                  onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                  placeholder="عنوان الصورة"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.titleEn') || 'Title (English)'}</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  placeholder="Image Title"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.linkUrl') || 'Link URL (Optional)'}</label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://example.com"
                />
                <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
                  {t('admin.linkUrlHint') || 'If provided, clicking the image will navigate to this URL'}
                </small>
              </div>
              <div className="form-group">
                <label>{t('admin.displayOrder') || 'Display Order'}</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
                <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
                  {t('admin.displayOrderHint') || 'Lower numbers appear first in the slider'}
                </small>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {t('admin.active') || 'Active'}
                </label>
                <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
                  {t('admin.activeHint') || 'Only active images will be shown on the website'}
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-btn">
                  {t('admin.cancel') || 'Cancel'}
                </button>
                <button type="submit" className="save-btn">
                  {t('admin.save') || 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHeroImages;

